/* eslint-disable fp/no-mutation */
import React, { useCallback, useState } from "react";
import { Tooltip } from "react-tooltip";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { prorbabilityToPercentage } from "utils/numbers";
import { buildCardData, generateCardImage } from "utils/cardGenerator";
import twitterXLogo from "styles/images/components/navbar/twitterXLogo.svg";

interface ShareButtonProps {
  resultData: {
    predicted_winner: number;
    p_team1_wins: number;
    p_team2_wins: number;
  };
  userTeam: string[];
  opponentTeam: string[];
  percentages: number[];
  opponentUsername?: string;
  userUsername?: string;
  isUserBlueSide?: boolean;
}

/**
 * Converts a data-URL to a Blob.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

function ShareButton({
  resultData,
  userTeam,
  opponentTeam,
  percentages: percentagesProp,
  opponentUsername: opponentUsernameProp,
  userUsername,
  isUserBlueSide,
}: ShareButtonProps) {
  const [status, setStatus] = useState<"idle" | "loading">("idle");
  const [tooltip, setTooltip] = useState("Share your draft on X");

  const percentages = percentagesProp.length
    ? percentagesProp
    : [
        prorbabilityToPercentage(resultData?.p_team1_wins),
        prorbabilityToPercentage(resultData?.p_team2_wins),
      ];
  const opponentUsername = opponentUsernameProp || "MyWorstEnemy";

  const buildTweetText = useCallback(() => {
    try {
      return "Colle le récap de ta draft déjà enregistré dans ton presse-papier 📋\n\n#Drafter @Drafter_one";
    } catch {
      return "";
    }
  }, []);

  const handleShare = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (status === "loading" || !resultData) return;
      setStatus("loading");
      setTooltip("Generating...");

      try {
        const tweetText = buildTweetText();

        // Generate card image via Canvas
        const data = await buildCardData({
          userCharacters: userTeam,
          opponentCharacters: opponentTeam,
          userUsername: userUsername || "You",
          opponentUsername,
          percentages,
          isUserBlueSide: isUserBlueSide ?? true,
        });
        const dataUrl = await generateCardImage(data);
        const imageBlob = dataUrlToBlob(dataUrl);

        // Copy image to clipboard, then open Twitter intent
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": imageBlob }),
          ]);
          setTooltip("Image copied! Paste it in your tweet 📋");
        } catch {
          setTooltip("Share your draft on X");
        }

        const encodedText = encodeURIComponent(tweetText);
        window.open(
          `https://twitter.com/intent/tweet?text=${encodedText}`,
          "_blank",
          "noopener,noreferrer",
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Share failed:", err);
        // Fallback: just open Twitter intent with text
        const tweetText = buildTweetText();
        if (tweetText) {
          const encodedText = encodeURIComponent(tweetText);
          window.open(
            `https://twitter.com/intent/tweet?text=${encodedText}`,
            "_blank",
            "noopener,noreferrer",
          );
        }
        setTooltip("Share your draft on X");
      }

      setStatus("idle");
      setTimeout(() => {
        setTooltip("Share your draft on X");
      }, 4000);
    },
    [
      status,
      resultData,
      buildTweetText,
      userTeam,
      opponentTeam,
      userUsername,
      opponentUsername,
      percentages,
      isUserBlueSide,
    ],
  );

  return (
    <>
      <motion.button
        className="twittButton"
        data-tooltip-id="shareButton"
        data-tooltip-content={tooltip}
        whileHover={{ scale: status === "loading" ? 1 : 1.1 }}
        whileTap={{ scale: status === "loading" ? 1 : 0.9 }}
        onClick={handleShare}
        disabled={status === "loading"}
        style={status === "loading" ? { opacity: 0.7, cursor: "wait" } : {}}
      >
        {status === "loading" ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : (
          <motion.img alt="X logo" src={twitterXLogo} />
        )}
      </motion.button>

      <Tooltip id="shareButton" />
    </>
  );
}

export default ShareButton;
